import { Directive, Input, OnDestroy, OnInit } from '@angular/core'
import { Subscription } from 'rxjs'
import { convertITrackToPlayerTrack, ISong, ITrack } from '../Interface/track'
import { PlayerLogic } from '../player/AngPlayer'
import { PlayStatus } from '../player/common/PlayerLogic'
import { SubscriberType } from '../player/common/SubscribingObject'
import { PlayerLogicTrackType, RepeatMode } from '../player/common/Types'
import { CartService } from '../services/cart.service'

@Directive()
export class PlayerListenerComponentDirective<T extends ITrack> implements OnInit, OnDestroy {
  // beat, that component has as constant beat
  @Input() fixedBeat: T | null = null

  public progress: number
  public loadProgress: number
  status: PlayStatus = PlayStatus.Stopped
  PlayStatus = PlayStatus

  private unsubscribers: SubscriberType[] = []
  private subscriptions: Subscription[] = []

  repeatMode: RepeatMode = 'off'
  isShuffleActive = false

  isSeeking = false
  seekingPositionSeconds = 0
  private lastSeekInputType: 'mouse' | 'touch' | null = null
  /**
   * Prevents the next progress bar update after manual seek to avoid immediate override
   * from timeupdate events.
   *
   * When true, the next 'timeupdate' from the player will be ignored,
   * ensuring that manual seeking reflects immediately in the UI.
   */
  private suppressNextProgressUpdate = false

  constructor(
    public player: PlayerLogic,
    protected cartService: CartService
  ) {}

  ngOnInit() {
    this.unsubscribers = [
      this.player.subscribe('play', this.onPlayBeat.bind(this)),
      this.player.subscribe('pause', this.onPauseBeat.bind(this)),
      this.player.subscribe('change-progress', this.redrawProgressBar.bind(this)),

      this.player.subscribe('timeupdate', this.progressBarUpdate.bind(this)),
      this.player.subscribe('progress', this.progressBarBufferUpdate.bind(this)),
      this.player.subscribe('ended', this.onEnded.bind(this))
    ]

    this.subscriptions = [
      this.player.repeatMode$.subscribe(mode => (this.repeatMode = mode)),
      this.player.isShuffleActive$.subscribe(active => (this.isShuffleActive = active))
    ]
  }

  ngOnDestroy() {
    this.unsubscribers.forEach(unsubscriber => unsubscriber())
    this.subscriptions.forEach(subscription => subscription.unsubscribe())
    this.cleanupSeekListeners()
  }

  progressBarBufferUpdate(data) {
    this.loadProgress = data.currentLoadPosition
  }

  progressBarUpdate(data: { currentPosition: number }) {
    if (this.suppressNextProgressUpdate) {
      this.suppressNextProgressUpdate = false
      return
    }
    if (!this.isSeeking) {
      this.progress = data.currentPosition
    }
  }

  onPauseBeat(beat: PlayerLogicTrackType<T>) {
    if (this.fixedBeat) {
      if (beat.id === this.fixedBeat.id) {
        this.status = PlayStatus.Paused
      }
    } else {
      this.status = PlayStatus.Paused
    }
  }

  currentTrackIsBeat() {
    return this.player.currentTrack.type === 'beat' || this.player.currentTrack.type === 'free-beat'
  }

  currentTrackIsSong() {
    return this.player.currentTrack.type === 'beat'
  }

  getTrackAsBeat() {
    return this.player.trackData as T
  }

  getTrackAsSong() {
    return this.player.trackData as ISong
  }

  play(e, beat: ITrack) {
    e.stopPropagation()
    this.player.play(convertITrackToPlayerTrack(beat), beat)
  }

  pause(e) {
    e.stopPropagation()
    this.player.pause()
  }

  onPlayBeat({ track, data }: { track: PlayerLogicTrackType<T>; data: any }) {
    // responsible only for one beat
    if (this.fixedBeat) {
      if (track.id === this.fixedBeat.id) {
        this.status = PlayStatus.Playing
      } else {
        this.status = PlayStatus.Stopped
      }
    } else {
      this.status = PlayStatus.Playing
    }
  }

  onPlayPauseClick(event: MouseEvent) {
    event.stopPropagation()
    if (this.player.status === PlayStatus.Playing) {
      this.status = PlayStatus.Paused
      this.player.pause()
    } else {
      this.status = PlayStatus.Playing
      this.player.continuePlayAfterPause()
    }
  }

  onRepeatClick() {
    this.player.toggleRepeatMode()
  }

  onShuffleClick() {
    this.player.toggleShuffle()
  }

  private onEnded(): void {
    this.repeatMode === 'one' && this.player.currentTrack
      ? this.player.play(this.player.currentTrack, this.player.currentTrack.originalData)
      : this.player.playNext()
  }

  onPlayPrevClick(event: MouseEvent) {
    event.stopPropagation()
    this.player.playPrev()
  }

  onPlayNextClick(event: MouseEvent) {
    event.stopPropagation()
    this.player.playNext()
  }

  wantToAddBeat(event: MouseEvent, trackId: string | number) {
    event.stopPropagation()
    this.cartService.wantAddBeat(trackId as number)
  }

  changeVolume(event) {
    const volume = Number(event.currentTarget.value) / 100
    this.player.changeVolume(volume)
  }

  onSeekStart(event: MouseEvent | TouchEvent): void {
    this.isSeeking = true
    this.updateSeekPreview(event)

    const isTouch = event instanceof TouchEvent
    this.lastSeekInputType = isTouch ? 'touch' : 'mouse'

    if (isTouch) {
      window.addEventListener('touchmove', this.onSeekMove, { passive: false })
      window.addEventListener('touchend', this.onSeekEnd)
    } else {
      window.addEventListener('mousemove', this.onSeekMove)
      window.addEventListener('mouseup', this.onSeekEnd)
    }
  }

  onSeekMove = (event: MouseEvent | TouchEvent): void => {
    if (this.isSeeking) {
      event.preventDefault()
      this.updateSeekPreview(event)
    }
  }

  onSeekEnd = (event: MouseEvent | TouchEvent): void => {
    if (!this.isSeeking) {
      return
    }
    this.isSeeking = false

    const percent = this.getSeekPercent(event)
    if (percent <= 0 && this.progress > 0) {
      this.cleanupSeekListeners()
      return
    }

    this.commitSeek(percent)
    this.cleanupSeekListeners()
  }

  onProgressBarClick(event: MouseEvent): void {
    const percent = this.getSeekPercent(event)
    this.commitSeek(percent)
  }

  /**
   * Gets the seek percentage based on interaction.
   * Must be overridden by components with progress bar references.
   */
  protected getSeekPercent(event: MouseEvent | TouchEvent): number {
    return this.progress
  }

  protected updateSeekPreview(event: MouseEvent | TouchEvent): void {
    const percent = this.getSeekPercent(event)
    this.redrawProgressBar(percent)
    this.seekingPositionSeconds = this.getTimeFromPercent(percent)
  }

  protected commitSeek(percent: number): void {
    this.player.setTrackPositionByPercent(percent)
    this.redrawProgressBar(percent)
    this.suppressNextProgressUpdate = true
  }

  redrawProgressBar(percent: number) {
    this.progress = percent
  }

  private cleanupSeekListeners(): void {
    if (this.lastSeekInputType === 'mouse') {
      window.removeEventListener('mousemove', this.onSeekMove)
      window.removeEventListener('mouseup', this.onSeekEnd)
    } else if (this.lastSeekInputType === 'touch') {
      window.removeEventListener('touchmove', this.onSeekMove)
      window.removeEventListener('touchend', this.onSeekEnd)
    }

    this.lastSeekInputType = null
  }

  /** @returns the string "current time / total time" to display */
  get formattedProgressTooltipTime(): string {
    return `${this.actualCurrentTime} / ${this.getFormattedTotalTime()}`
  }

  /** @returns the formatted time for the current position (current or selected during se */
  get actualCurrentTime(): string {
    const seconds = this.isSeeking ? this.seekingPositionSeconds : this.player.getCurrentTimeSeconds()
    return this.formatTime(seconds)
  }

  /** @returns formatted string like '3:45' from track duration in seconds */
  getFormattedTotalTime(): string {
    return this.formatTime(this.player.getTrackDurationSeconds())
  }

  /** Converts seconds into mm:ss string */
  formatTime(seconds: number): string {
    const min = Math.floor(seconds / 60)
    const sec = Math.floor(seconds % 60)
    return `${min}:${sec < 10 ? '0' : ''}${sec}`
  }

  getTimeFromPercent(percent: number): number {
    return (percent / 100) * this.player.getTrackDurationSeconds()
  }
}
